import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Helper to format date into DD/MM/YYYY
const formatDateDashes = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper to format date into DD/MM/YYYY (with slashes)
const formatDateSlashes = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const Form9RevisedReport = () => {
  const dispatch = useDispatch();

  // Setup session state from localStorage
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })()
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);

  // States
  const [firmList, setFirmList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Sync session changes from storage and sessionChange events
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, []);

  // Fetch initial base listings
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
  }, [dispatch]);

  // Load report data when inputs change
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId) {
      loadReportData(firmId, searchTerm);
    } else {
      setEmployees([]);
      setReportData(null);
    }
  }, [sessionFirm, searchTerm]);

  const loadReportData = async (firmId, searchQuery) => {
    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append("F_FirmMaster", firmId);
      fd.append("EmployeeSearch", searchQuery || "");

      const records = await Fn_GetReport(
        dispatch,
        () => {},
        "data",
        "GetPFForm9/0/token",
        { arguList: { id: 0, formData: fd } },
        true
      );

      let parsedData = null;
      if (records && records.length > 0) {
        const firstKey = Object.keys(records[0])[0];
        if (firstKey !== undefined && firstKey !== null) {
          const jsonString = records.map(row => row[firstKey] || "").join("");
          if (jsonString) {
            const rawObj = JSON.parse(jsonString);
            parsedData = rawObj.data || rawObj;
          }
        }
      }
      setReportData(parsedData);
      setEmployees(parsedData?.Records || []);
    } catch (err) {
      console.error("Error loading employee data:", err);
      setEmployees([]);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Find Firm Code No. (which is PF No. on the branch)
  const getFirmPfNo = () => {
    if (reportData?.FirmDetails?.PFNo) return reportData.FirmDetails.PFNo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  // Process rows directly from API records
  const processedRows = employees.map((emp, index) => {
    const leftDate = emp.LeftDate || emp.DateOfLeaving || "";
    return {
      sno: emp.Sno || (index + 1),
      acNo: emp.AcNo || emp.SerialNo || "",
      uan: emp.UAN || "",
      cardNo: emp.EmployeeCode || emp.CardNo || "",
      name: (emp.EmployeeName || emp.Name || "").toUpperCase(),
      fatherName: (emp.FatherName || "").toUpperCase(),
      dob: formatDateDashes(emp.DateOfBirth),
      sex: emp.Sex ? emp.Sex.substring(0, 1).toUpperCase() : "",
      dateOfMembership: formatDateDashes(emp.PFJoinDate || emp.JoinDate),
      totalPeriodOfService: formatDateSlashes(emp.JoinDate),
      dateOfLeaving: formatDateDashes(leftDate),
      department: (emp.DepartmentName || emp.Department || "").toUpperCase(),
      id: emp.Id || emp.Sno
    };
  });

  // Filter list by search term
  const filteredRows = processedRows.filter(row => {
    const term = searchTerm.toLowerCase();
    return (
      row.name.toLowerCase().includes(term) ||
      row.fatherName.toLowerCase().includes(term) ||
      row.uan.toLowerCase().includes(term) ||
      row.acNo.toString().toLowerCase().includes(term) ||
      row.department.toLowerCase().includes(term)
    );
  });

  // PDF Export matching rptForm9Revised.pdf layout (Landscape A4)
  const exportToPDF = () => {
    if (filteredRows.length === 0) {
      alert("No data available to export.");
      return;
    }

    const pdf = new jsPDF("l", "mm", "a4");

    // Title Block
    pdf.setFontSize(12);
    pdf.setFont("Helvetica", "bold");
    pdf.text(sessionFirm?.Name?.toUpperCase() || "", 148, 12, { align: "center" });

    pdf.setFontSize(9.5);
    pdf.text("Register of FORM NO. 9 (REVISED)", 148, 17, { align: "center" });

    pdf.setFont("Helvetica", "bold");
    pdf.text(`Code No :  ${getFirmPfNo()}`, 15, 22);

    const body = filteredRows.map(row => [
      row.sno,
      `${row.acNo}\n${row.uan ? row.uan : ""}\n${row.cardNo ? row.cardNo : ""}`,
      `${row.name}\n${row.fatherName}`,
      row.dob,
      row.sex,
      row.dateOfMembership,
      row.totalPeriodOfService,
      "", // Initials of H.C.
      row.dateOfLeaving,
      "", // Remarks and initials on settlement
      row.department,
      "" // Despatch Date
    ]);

    autoTable(pdf, {
      head: [[
        "Sr. No.",
        "A/c No.\nUAN No.\nCard No.",
        "EmployeeName\nFatherName",
        "Date\nof\nBirth",
        "Sex",
        "Date of\nMembership",
        "Total Period of\nService on Date of\nMembership",
        "Initials of H.C.",
        "Date of Leaving\nService",
        "Remarks and initials\non settlement : D.C.\nH.C. A.O.",
        "Department",
        "Despatch Date"
      ]],
      body,
      startY: 25,
      margin: { left: 10, right: 10 },
      styles: {
        fontSize: 7.5,
        cellPadding: 1.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.05,
        textColor: [0, 0, 0],
        valign: "middle"
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "left",
        valign: "top"
      },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 26 },
        2: { cellWidth: 42 },
        3: { cellWidth: 20 },
        4: { cellWidth: 10 },
        5: { cellWidth: 22 },
        6: { cellWidth: 28 },
        7: { cellWidth: 22 },
        8: { cellWidth: 22 },
        9: { cellWidth: 32 },
        10: { cellWidth: 32 },
        11: { cellWidth: 22 }
      },
      theme: "grid"
    });

    pdf.save(`Form_9_Revised_Report_${sessionFirm?.Name}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-form9-container, #print-form9-container * {
        visibility: visible !important;
      }
      #print-form9-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff;
        color: #000;
        font-family: Arial, sans-serif;
      }
      .no-print {
        display: none !important;
      }
      .form9-print-table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 15px;
      }
      .form9-print-table th, .form9-print-table td {
        border: 1px solid #000;
        padding: 4px;
        font-size: 8px;
        text-align: left;
        vertical-align: top;
      }
      .form9-print-table th {
        font-weight: bold;
        background: #f2f2f2;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="Form 9 Revised" motherMenu="Reports" pageContent="Register of Form 9" />
      </div>

      {/* Filters Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm */}
            <Col lg={5} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Search */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Employee</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Search by Name, UAN, Account No., Dept..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>

            <Col lg={3} md={12} className="d-flex gap-2 justify-content-end">
              <Button variant="danger" style={{ height: "38px" }} onClick={exportToPDF}>PDF</Button>
              <Button variant="primary" style={{ height: "38px" }} onClick={() => window.print()}>Print</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Firm Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm** to load the Form 9 Register.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* Web View */}
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px", overflow: "hidden" }}>
              <Card.Header className="bg-white border-bottom py-3 px-4">
                <div className="text-center">
                  <h4 className="fw-bold mb-1 text-dark fs-15">{sessionFirm?.Name}</h4>
                  <div className="fw-bold text-secondary fs-13">Register of FORM NO. 9 (REVISED)</div>
                  <div className="text-muted fs-12">Code No : <strong>{getFirmPfNo()}</strong></div>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Loading Form 9 Details...</div>
                  </div>
                ) : filteredRows.length === 0 ? (
                  <div className="text-center text-muted p-5">No PF records found.</div>
                ) : (
                  <div style={{ overflowX: "auto", display: "block", width: "100%" }}>
                    <table className="table table-bordered table-striped table-hover mb-0 text-center text-nowrap" style={{ fontSize: "11px", minWidth: "1250px", width: "100%" }}>
                      <thead className="table-light text-dark">
                        <tr>
                          <th style={{ width: "4%" }}>Sr. No.</th>
                          <th style={{ width: "12%" }}>A/c No.<br />UAN No.<br />Card No.</th>
                          <th style={{ width: "20%" }}>EmployeeName<br />FatherName</th>
                          <th style={{ width: "10%" }}>Date of Birth</th>
                          <th style={{ width: "4%" }}>Sex</th>
                          <th style={{ width: "10%" }}>Date of Membership</th>
                          <th style={{ width: "12%" }}>Total Period of Service on Date of Membership</th>
                          <th style={{ width: "8%" }}>Initials of H.C.</th>
                          <th style={{ width: "10%" }}>Date of Leaving Service</th>
                          <th style={{ width: "12%" }}>Remarks and initials on settlement</th>
                          <th style={{ width: "12%" }}>Department</th>
                          <th style={{ width: "8%" }}>Despatch Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((row) => (
                          <tr key={row.id}>
                            <td>{row.sno}</td>
                            <td className="text-start">
                              <strong>{row.acNo}</strong><br />
                              <span className="text-muted fs-10">{row.uan}</span><br />
                              <span className="text-muted fs-10">{row.cardNo}</span>
                            </td>
                            <td className="text-start">
                              <strong>{row.name}</strong><br />
                              <span className="text-secondary">{row.fatherName}</span>
                            </td>
                            <td>{row.dob}</td>
                            <td>{row.sex}</td>
                            <td>{row.dateOfMembership}</td>
                            <td>{row.totalPeriodOfService}</td>
                            <td></td>
                            <td className="text-danger fw-semibold">{row.dateOfLeaving}</td>
                            <td></td>
                            <td className="text-start">{row.department}</td>
                            <td></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Print View Layout */}
          <div id="print-form9-container" className="d-none">
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px" }}>
              {sessionFirm?.Name?.toUpperCase()}
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "10px", margin: "4px 0" }}>
              Register of FORM NO. 9 (REVISED)
            </div>
            <div style={{ textAlign: "left", fontSize: "9px", margin: "4px 10px" }}>
              Code No : {getFirmPfNo()}
            </div>

            <table className="form9-print-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>A/c No.<br />UAN No.<br />Card No.</th>
                  <th>EmployeeName<br />FatherName</th>
                  <th>Date of Birth</th>
                  <th>Sex</th>
                  <th>Date of Membership</th>
                  <th>Total Period of Service on Date of Membership</th>
                  <th>Initials of H.C.</th>
                  <th>Date of Leaving Service</th>
                  <th>Remarks and initials on settlement</th>
                  <th>Department</th>
                  <th>Despatch Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => (
                  <tr key={row.id}>
                    <td>{row.sno}</td>
                    <td>
                      <strong>{row.acNo}</strong><br />
                      {row.uan}<br />
                      {row.cardNo}
                    </td>
                    <td>
                      <strong>{row.name}</strong><br />
                      {row.fatherName}
                    </td>
                    <td>{row.dob}</td>
                    <td>{row.sex}</td>
                    <td>{row.dateOfMembership}</td>
                    <td>{row.totalPeriodOfService}</td>
                    <td></td>
                    <td>{row.dateOfLeaving}</td>
                    <td></td>
                    <td>{row.department}</td>
                    <td></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default Form9RevisedReport;
